import {
  Button,
  FormControl,
  FormLabel,
  Grid,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ConditionalRenderer,
  useLanguageTranslator,
} from "../../../../../components";
import { useAuthSession } from "../../../../../components/AuthSessionProvider";
import { useNotify } from "../../../../../components/@common/NotificationsProvider";
import { ToastContainer } from "react-toastify";
import { toastHandler } from "../../../../../utils/helpers";
import { toastTypesKeys } from "../../../../../utils/constants";

// Define the GraphQL mutation
const CREATE_INSTITUTION = gql`
  mutation CreateInstitution($name: String!) {
    institutionCreate(input: { institutionInput: { name: $name } }) {
      clientMutationId
      institution {
        id
        name
      }
    }
  }
`;

const UPDATE_INSTITUTION = gql`
  mutation UpdateInstitution($id: ID!, $name: String!) {
    institutionUpdate(input: { id: $id, institutionInput: { name: $name } }) {
      clientMutationId
      institution {
        id
        name
      }
    }
  }
`;

const GET_INSTITUTION = gql`
  query GetInstitution($id: ID!) {
    institution(id: $id) {
      id
      name
    }
  }
`;

function InstitutionsAction({ isUpdate = false }) {
  const navigate = useNavigate();
  const notify = useNotify();
  const { user } = useAuthSession() as any;
  const { translator, currentLanguage } = useLanguageTranslator();

  const [searchParams] = useSearchParams();
  const institution_id = searchParams.get("id");

  const { data: institutionData } = useQuery(GET_INSTITUTION, {
    variables: { id: institution_id },
    skip: !institution_id,
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const [createInstituion, { data, loading, error }] = useMutation(
    CREATE_INSTITUTION,
    {
      context: {
        headers: {
          "accept-language": currentLanguage || "en",
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const [updateInstitution] = useMutation(UPDATE_INSTITUTION, {
    context: {
      headers: {
        "accept-language": currentLanguage || "en",
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const initialValues: any = useMemo(() => {
    if (!institutionData?.institution) return undefined;

    const { name } = institutionData?.institution;

    return {
      institution_name: name,
    };
  }, [institutionData]);

  const form = useForm<any>({
    defaultValues: {
      institution_name: "",
    },
    values: initialValues,
  });

  const handleFinish = useCallback(async (data: any, actionType: any) => {
    if (actionType === "update") {
      try {
        const response = await updateInstitution({
          variables: {
            id: Number(institution_id),
            name: data?.institution_name,
          },
        });
        if (response.data) {
          notify.success(`${translator(`Institution`)} Updated successfuly!`);
          setTimeout(() => {
            navigate("/admin/institutions");
            return;
          }, 1000);
        }
      } catch (err) {}
      return;
    }

    try {
      const response = await createInstituion({
        variables: {
          name: data?.institution_name,
        },
      });
      if (actionType === "save") {
        if (response.data) {
          notify.success(`${translator(`Institution`)} Created successfuly!`);
          setTimeout(() => {
            navigate("/admin/institutions");
            return;
          }, 1000);
        }
      } else if (actionType === "saveAndAddNew") {
        if (response.data) {
          notify.success(`${translator(`Institution`)} Created successfuly!`);
          setTimeout(() => {
            form?.reset();
            return;
          }, 1000);
        }
      }
    } catch (err) {
      const errorCatch = JSON.stringify(err);
      const parsedError = JSON.parse(errorCatch);

      toastHandler(parsedError?.message || "", toastTypesKeys.error);
    }
  }, []);

  return (
    <>
      <ToastContainer />
      <form onSubmit={form.handleSubmit((data) => handleFinish(data, "save"))}>
        <Grid
          container
          px={4}
          sx={{ backgroundColor: "#ffffff", borderRadius: "10px" }}
          spacing={2}
        >
          <Grid item xs={12} sm={6} lg={6}>
            <FormControl required disabled={false} fullWidth margin="normal">
              <FormLabel>
                {translator(`Institution`)}{" "}
                {translator("dashboardTableHead.Name")}
              </FormLabel>
              <OutlinedInput
                {...form?.register("institution_name", {
                  required: "Required!",
                })}
              />
              {/* <FormHelperText>{errors.title?.message}</FormHelperText> */}
            </FormControl>
          </Grid>
        </Grid>
        <Stack
          sx={{ backgroundColor: "#ffffff" }}
          direction={"row"}
          justifyContent={"flex-end"}
          gap={2}
          px={4}
          py={4}
        >
          <Link
            to="/admin/institutions"
            onClick={() => form?.reset()}
            style={{
              backgroundColor: "#e0e0e0",
              padding: "8px 12px",
              color: "#989C9F",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {translator("Cancel")}
          </Link>
          <Button
            type="button"
            onClick={form.handleSubmit((data) =>
              handleFinish(data, isUpdate ? "update" : "save")
            )}
            style={{
              backgroundColor: "#00AAB0",
              padding: "8px 12px",
              color: "white",
              borderRadius: "10px",
              fontSize: "14px",
              textDecoration: "none",
            }}
          >
            {isUpdate
              ? translator("generic.Update")
              : translator("generic.Save")}
          </Button>
          <ConditionalRenderer condition={!isUpdate}>
            <Button
              type="button"
              onClick={form.handleSubmit((data) =>
                handleFinish(data, "saveAndAddNew")
              )}
              style={{
                backgroundColor: "#F6A000",
                padding: "8px 12px",
                color: "white",
                borderRadius: "10px",
                fontSize: "14px",
                textDecoration: "none",
              }}
            >
              {translator("generic.Save")} & {translator("generic.Add New")}
            </Button>
          </ConditionalRenderer>
        </Stack>
      </form>
    </>
  );
}

export { InstitutionsAction };
